import React from 'react';
import { User, MapPin, Car, Check, Clock, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DriverOnboardingTrackerProps {
    completedStep: number;
    className?: string;
}

interface OnboardingStepDef {
    stepNumber: number;
    title: string;
    screenName: string;
    description: string;
    icon: React.ReactNode;
}

const STEPS: OnboardingStepDef[] = [
    {
        stepNumber: 1,
        title: 'Personal Info',
        screenName: 'PersonalInfoScreen',
        description: 'Full name, email, phone & identity details',
        icon: <User className="w-5 h-5" />,
    },
    {
        stepNumber: 2,
        title: 'Address Details',
        screenName: 'AddressScreen',
        description: 'Registered addresses, city & location',
        icon: <MapPin className="w-5 h-5" />,
    },
    {
        stepNumber: 3,
        title: 'Vehicle Details',
        screenName: 'VehicleDetails',
        description: 'Vehicle type, number plate & documents',
        icon: <Car className="w-5 h-5" />,
    },
];

export const DriverOnboardingTracker: React.FC<DriverOnboardingTrackerProps> = ({
    completedStep,
    className,
}) => {
    // Clamped step count between 0 and 3
    const safeCompletedStep = Math.max(0, Math.min(3, Number(completedStep || 0)));

    const getStatusMessage = () => {
        if (safeCompletedStep === 0) {
            return {
                title: 'Pending at Step 1: Personal Info',
                description: 'The driver started registration and has not yet completed the Personal Information screen.',
                screen: 'PersonalInfoScreen',
            };
        }
        if (safeCompletedStep === 1) {
            return {
                title: 'Pending at Step 2: Address Details',
                description: 'Step 1 (PersonalInfoScreen) is completed. The driver is currently pending on the Address screen.',
                screen: 'AddressScreen',
            };
        }
        if (safeCompletedStep === 2) {
            return {
                title: 'Pending at Step 3: Vehicle Details',
                description: 'Steps 1 & 2 are completed. The driver is currently pending on the Vehicle Details screen.',
                screen: 'VehicleDetails',
            };
        }
        return {
            title: 'All Steps Completed',
            description: 'All 3 onboarding screens have been filled out. Awaiting final driver submission.',
            screen: 'Completed',
        };
    };

    const statusInfo = getStatusMessage();

    return (
        <div className={cn("bg-card border border-border/80 rounded-2xl p-6 shadow-sm space-y-6", className)}>
            {/* Header info */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-border/60">
                <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                        Draft Mode
                    </span>
                    <h3 className="text-base font-bold text-foreground">
                        Driver Onboarding Progress
                    </h3>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground bg-secondary/15 px-3 py-1.5 rounded-xl">
                    <Clock className="w-3.5 h-3.5 text-amber-600" />
                    <span>
                        Progress: <strong className="text-foreground">{safeCompletedStep}</strong> of 3 Steps Completed
                    </span>
                </div>
            </div>

            {/* Stepper visual */}
            <div className="relative pt-2 pb-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
                    {STEPS.map((step, index) => {
                        const isCompleted = index < safeCompletedStep;
                        const isCurrent = index === safeCompletedStep;
                        const isUpcoming = index > safeCompletedStep;

                        return (
                            <div
                                key={step.screenName}
                                className={cn(
                                    "relative rounded-xl p-4 border transition-all flex flex-col justify-between gap-3",
                                    isCurrent && "border-amber-400/80 bg-amber-50/40 dark:bg-amber-950/20 ring-2 ring-amber-400/20 shadow-sm",
                                    isCompleted && "border-emerald-300/80 bg-emerald-50/30 dark:bg-emerald-950/20",
                                    isUpcoming && "border-border/50 bg-secondary/5 opacity-70"
                                )}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    {/* Circle Icon */}
                                    <div
                                        className={cn(
                                            "w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 transition-transform",
                                            isCompleted && "bg-emerald-600 text-white shadow-sm shadow-emerald-500/30",
                                            isCurrent && "bg-amber-500 text-white ring-4 ring-amber-100 dark:ring-amber-900/50 shadow-sm shadow-amber-500/30 animate-pulse",
                                            isUpcoming && "bg-secondary/40 text-muted-foreground"
                                        )}
                                    >
                                        {isCompleted ? <Check className="w-5 h-5 stroke-[2.5]" /> : step.icon}
                                    </div>

                                    {/* Step status badge */}
                                    <span
                                        className={cn(
                                            "px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase whitespace-nowrap",
                                            isCompleted && "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/80 dark:text-emerald-300",
                                            isCurrent && "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200",
                                            isUpcoming && "bg-secondary/40 text-muted-foreground"
                                        )}
                                    >
                                        {isCompleted ? 'Completed' : isCurrent ? 'Draft Pending' : 'Upcoming'}
                                    </span>
                                </div>

                                <div>
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-semibold">
                                        <span>Step {step.stepNumber}</span>
                                        <span>•</span>
                                        <code className="text-[11px] font-mono font-medium text-foreground/80 bg-background/80 px-1.5 py-0.5 rounded border border-border/50">
                                            {step.screenName}
                                        </code>
                                    </div>
                                    <p className="font-bold text-sm text-foreground mt-1">
                                        {step.title}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                                        {step.description}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Bottom Status Callout */}
            <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-xs">
                    <p className="font-bold text-amber-900 dark:text-amber-200">
                        {statusInfo.title}
                    </p>
                    <p className="text-amber-800/90 dark:text-amber-300/90 mt-0.5">
                        {statusInfo.description}
                    </p>
                </div>
            </div>
        </div>
    );
};
